import { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useGetClientsQuery } from 'api/endpoints/admin/client'
import { useAdminListUsersQuery, useAdminReassignAssetOwnerMutation } from 'api'
import { Client } from 'api/types/models/client'
import { ConfirmationModal } from 'components/modals/confirmation'
import ComboBox from 'components/form/combobox'
import FormError from 'components/error'
import Toggle from 'components/form/toggle'
import { useToast } from 'hooks/useToast'

type ReassignOwnerModalProps = {
  assetId: number
  isOpen: boolean
  onClose: () => void
  // The asset's current client, used to pre-populate the owner list
  currentClientId?: number | null
  // The asset's current owner, pre-selected so the admin can see the change
  currentUserId?: number | null
}

type FieldErrors = {
  client_id?: string
  user_id?: string
}

const ReassignOwnerModal = ({
  assetId,
  isOpen,
  onClose,
  currentClientId,
  currentUserId,
}: ReassignOwnerModalProps) => {
  const { successToast, errorToast } = useToast()

  const [clientId, setClientId] = useState<number | undefined>(currentClientId ?? undefined)
  const [userId, setUserId] = useState<number | undefined>(currentUserId ?? undefined)
  const [wipeData, setWipeData] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  // Reset the form each time the modal is opened
  useEffect(() => {
    if (isOpen) {
      setClientId(currentClientId ?? undefined)
      setUserId(currentUserId ?? undefined)
      setWipeData(false)
      setFieldErrors({})
    }
  }, [isOpen, currentClientId, currentUserId])

  const { data: clients, isLoading: isClientsLoading } = useGetClientsQuery({})
  const { data: users, isLoading: isUsersLoading } = useAdminListUsersQuery(
    { clientId },
    { skip: !clientId },
  )

  const [reassignOwner, { isLoading: isReassigning }] = useAdminReassignAssetOwnerMutation()

  const clientOptions = useMemo(() => {
    const unpaginatedClients = clients as Client[]
    return unpaginatedClients?.map((client) => ({ value: client.id, text: client.display_name }))
  }, [clients])

  const userOptions = useMemo(() => {
    return users?.users.map((user) => ({
      value: user.id,
      text: `${user.first_name} ${user.last_name}`,
    }))
  }, [users])

  const handleConfirm = async () => {
    const errors: FieldErrors = {}
    if (!clientId) errors.client_id = 'A client is required'
    if (!userId) errors.user_id = 'An owner is required'

    if (!clientId || !userId) {
      setFieldErrors(errors)
      return
    }

    await reassignOwner({
      id: assetId,
      client_id: clientId,
      user_id: userId,
      wipe_data: wipeData,
    })
      .unwrap()
      .then(() => {
        successToast({
          title: 'Owner Reassigned',
          message: 'The asset has been reassigned to the new owner',
        })
        onClose()
      })
      .catch((err) => {
        if (err?.status === 422 && Array.isArray(err?.data?.errors)) {
          const mapped: FieldErrors = {}
          err.data.errors.forEach((error: { field: string, message: string }) => {
            if (error.field === 'client_id' || error.field === 'user_id') {
              mapped[error.field] = error.message
            }
          })
          setFieldErrors(mapped)

          // Surface a toast for errors that aren't tied to a specific field
          if (Object.keys(mapped).length === 0) {
            errorToast({
              title: 'Reassign Failed',
              message: err.data.errors[0]?.message ?? 'Unable to reassign this asset',
            })
          }
          return
        }

        errorToast({
          title: 'Reassign Failed',
          message: 'Unable to reassign this asset at the moment, please try again later',
        })
      })
  }

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      title="Reassign Owner"
      btnTxt="Reassign Owner"
      isLoading={isReassigning}
      onConfirm={handleConfirm}
      renderContent={() => (
        <div className="space-y-4">
          <p className="text-sm text-primary-gray">
            Move this asset to a new owner. Review the effects below before confirming.
          </p>

          <div>
            <label className="block mb-1 text-sm font-medium" htmlFor="reassign_client">Client</label>
            <ComboBox
              isLoading={isClientsLoading}
              defaultValue={clientId}
              onChange={(option) => {
                setClientId(option ? (option.value as number) : undefined)
                setUserId(undefined) // Owner must belong to the selected client
                setFieldErrors((prev) => ({ ...prev, client_id: undefined }))
              }}
              options={clientOptions}
              placeholder="Search for a client"
            />
            {fieldErrors.client_id && <FormError text={fieldErrors.client_id} className="mt-1 !text-left" />}
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium" htmlFor="reassign_owner">Owner</label>
            <ComboBox
              key={`reassign-owner-${clientId}`} // Force re-create when the client changes
              isLoading={isUsersLoading}
              disabled={!clientId}
              defaultValue={userId}
              onChange={(option) => {
                setUserId(option ? (option.value as number) : undefined)
                setFieldErrors((prev) => ({ ...prev, user_id: undefined }))
              }}
              options={userOptions}
              placeholder="Search for an owner"
            />
            {fieldErrors.user_id && <FormError text={fieldErrors.user_id} className="mt-1 !text-left" />}
          </div>

          <div className="flex items-start justify-between gap-4 pt-2">
            <div>
              <p className="text-sm font-medium">Wipe previous owner&apos;s data</p>
              <p className="text-xs text-primary-gray">
                Hand the new owner a clean slate instead of the existing data. See the effects below.
              </p>
            </div>
            <Toggle checked={wipeData} onChange={setWipeData} />
          </div>

          <div
            className={classNames('rounded-md border p-3 text-sm', {
              'border-red-200 bg-red-50 text-red-800': wipeData,
              'border-green-200 bg-green-50 text-green-800': !wipeData,
            })}
          >
            <p className="mb-2 font-semibold">What happens</p>
            <ul className="space-y-2">
              {
                wipeData ? (
                  <>
                    <li className="flex gap-2">
                      <span aria-hidden className="font-semibold">✗</span>
                      <span>
                        <span className="font-medium">Removed</span> — the previous owner&apos;s
                        devices, third-party viewers, linked files, custom &amp; timed messages, and
                        all scan / access history &amp; stats
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span aria-hidden className="font-semibold">↻</span>
                      <span>
                        <span className="font-medium">Reset</span> — theme, description, location,
                        visibility and notification settings
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span aria-hidden className="font-semibold">＋</span>
                      <span>
                        <span className="font-medium">Fresh start</span> — a blank default message
                        and the new owner&apos;s device
                      </span>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex gap-2">
                      <span aria-hidden className="font-semibold">✓</span>
                      <span>
                        <span className="font-medium">Kept</span> — third-party viewers, linked
                        files, custom &amp; timed messages, and the asset&apos;s scan / access history
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span aria-hidden className="font-semibold">↻</span>
                      <span>
                        <span className="font-medium">Replaced</span> — the owner device, so the
                        previous owner can no longer access the asset
                      </span>
                    </li>
                  </>
                )
              }
            </ul>
          </div>
        </div>
      )}
    />
  )
}

export default ReassignOwnerModal
